import { useEffect, useState } from "react";
import type { ApprovalItem } from "@/types/approval";

type ActionKind = "approve" | "reject" | "comment" | "send";

interface Props {
  item: ApprovalItem;
  pending: ActionKind | null;
  onApprove: () => void;
  onReject: () => void;
  onComment: () => void;
  onSend: () => void; // gmailDraftId 入り案件のみ呼ばれる
}

export function ApprovalCard({
  item,
  pending,
  onApprove,
  onReject,
  onComment,
  onSend,
}: Props) {
  const hasDraft = !!item.gmailDraftId;
  const [confirming, setConfirming] = useState<"approve" | "reject" | null>(null);
  const disabled = !!pending;

  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(null), 3000);
    return () => clearTimeout(t);
  }, [confirming]);

  function handleApprove() {
    if (hasDraft) {
      // 外部送信を伴う承認は SendConfirmModal 経由（モーダルで二段確認）
      onSend();
      return;
    }
    if (confirming === "approve") {
      setConfirming(null);
      onApprove();
    } else {
      setConfirming("approve");
    }
  }
  function handleReject() {
    if (confirming === "reject") {
      setConfirming(null);
      onReject();
    } else {
      setConfirming("reject");
    }
  }

  const approveLabel = hasDraft
    ? pending === "send"
      ? "送信中…"
      : "✉️ 送信して承認"
    : pending === "approve"
      ? "送信中…"
      : confirming === "approve"
        ? "もう一度タップで確定"
        : "✅ 承認";

  return (
    <article className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center justify-between gap-2">
        <span className="rounded bg-secondary px-2 py-0.5 text-xs text-secondary-foreground">
          {item.kind || "未分類"}
        </span>
        <div className="flex items-center gap-1">
          {hasDraft && (
            <span
              className="rounded bg-primary/15 px-2 py-0.5 text-xs text-primary"
              title={`gmail_draft_id=${item.gmailDraftId}`}
            >
              ✉️ draft
            </span>
          )}
          {item.due && <span className="text-xs text-muted-foreground">⏰ {item.due}</span>}
        </div>
      </div>
      <h2 className="mt-2 font-medium leading-snug">{item.title || "(無題)"}</h2>
      {item.requester && (
        <p className="mt-1 text-xs text-muted-foreground">起票：{item.requester}</p>
      )}
      <div className="mt-3 flex gap-2">
        <button
          disabled={disabled}
          onClick={handleApprove}
          className="flex-1 rounded-lg bg-primary text-primary-foreground py-2 text-sm font-medium active:opacity-80 disabled:opacity-50"
        >
          {approveLabel}
        </button>
        <button
          disabled={disabled}
          onClick={handleReject}
          className="flex-1 rounded-lg bg-destructive text-destructive-foreground py-2 text-sm font-medium active:opacity-80 disabled:opacity-50"
        >
          {pending === "reject"
            ? "送信中…"
            : confirming === "reject"
              ? "もう一度タップで確定"
              : "❌ 却下"}
        </button>
        <button
          disabled={disabled}
          onClick={onComment}
          className="rounded-lg bg-secondary text-secondary-foreground px-3 py-2 text-sm font-medium active:opacity-80 disabled:opacity-50"
          aria-label="コメント"
        >
          {pending === "comment" ? "…" : "💬"}
        </button>
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <a
          href={item.notionUrl}
          target="_blank"
          rel="noreferrer"
          className="underline-offset-2 hover:underline"
        >
          Notionで開く →
        </a>
        {item.slackUrl && (
          <a
            href={item.slackUrl}
            target="_blank"
            rel="noreferrer"
            className="underline-offset-2 hover:underline"
          >
            Slack
          </a>
        )}
      </div>
    </article>
  );
}
